import { Shield, Key, Save, Loader2, QrCode as QrIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import QRCode from "qrcode";

export default function AdminSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Password Change States
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  // 2FA States
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [is2FASetupOpen, setIs2FASetupOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [factorId, setFactorId] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);

  useEffect(() => {
    async function fetchProfile() {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', user.id)
          .single();
          
        if (error) throw error;
        
        setName(data?.name || "");
        setEmail(user.email || "");

        // Check MFA Status
        const { data: mfaData, error: mfaError } = await supabase.auth.mfa.listFactors();
        if (mfaError) throw mfaError;

        const totpFactor = mfaData.totp.find(f => f.status === 'verified');
        setIs2FAEnabled(!!totpFactor);
        
        if (totpFactor) {
          setFactorId(totpFactor.id);
        }

      } catch (error) {
        console.error('Error fetching profile or MFA status:', error);
        toast.error(t('admin.settings.toast.loadError'));
      } finally {
        setLoading(false);
      }
    }
    
    fetchProfile();
  }, [user, t]);

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    
    try {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ name })
        .eq('id', user.id);

      if (profileError) throw profileError;

      if (email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({ email });
        if (emailError) throw emailError;
        toast.success(t('admin.settings.toast.emailValidSent'));
      }

      toast.success(t('admin.settings.toast.profileUpdated'));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : undefined;
      console.error('Error updating profile:', error);
      toast.error(message || t('admin.settings.toast.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error(t('admin.settings.toast.passwordMismatch'));
      return;
    }
    if (newPassword.length < 6) {
      toast.error(t('admin.settings.toast.passwordMinLength'));
      return;
    }

    setPasswordLoading(true);
    try {
      const { error } = await supabase.functions.invoke('admin-set-password', {
        body: { newPassword }
      });
      
      if (error) {
         let errMsg = error.message;
         try {
             const body = JSON.parse(error.message);
             if (body.error) errMsg = body.error;
         } catch (e) {
             // ignore
         }
         throw new Error(errMsg);
      }
      
      toast.success(t('admin.settings.toast.passwordSetSuccess'));
      setIsPasswordOpen(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : undefined;
      console.error('Error setting password:', error);
      toast.error(message || t('admin.settings.toast.passwordSetFailed'));
    } finally {
      setPasswordLoading(false);
    }
  };

  // 2FA Logic
  const handleEnable2FA = async () => {
    setSetupLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
      });

      if (error) throw error;

      setFactorId(data.id);
      
      const url = await QRCode.toDataURL(data.totp.uri);
      setQrCodeUrl(url);
      setIs2FASetupOpen(true);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : undefined;
      console.error('Error enrolling 2FA:', error);
      toast.error(message || t('admin.settings.toast.enrollError'));
    } finally {
      setSetupLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error(t('admin.settings.toast.enter6Digit'));
      return;
    }

    setSetupLoading(true);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;

      const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verifyCode,
      });

      if (verifyError) throw verifyError;

      toast.success(t('admin.settings.toast.enabled2FA'));
      setIs2FAEnabled(true);
      setIs2FASetupOpen(false);
      setVerifyCode("");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : undefined;
      console.error('Error verifying 2FA:', error);
      toast.error(message || t('admin.settings.toast.verifyFailed'));
    } finally {
      setSetupLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!confirm(t('admin.settings.toast.disableConfirm'))) return;

    try {
      const { data: mfaData, error: mfaError } = await supabase.auth.mfa.listFactors();
      if (mfaError) throw mfaError;

      const totpFactor = mfaData.totp.find(f => f.status === 'verified');
      if (!totpFactor) {
        setIs2FAEnabled(false);
        return;
      }

      const { error } = await supabase.auth.mfa.unenroll({
        factorId: totpFactor.id,
      });

      if (error) throw error;

      toast.success(t('admin.settings.toast.disabled2FA'));
      setIs2FAEnabled(false);
      setFactorId("");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : undefined;
      console.error('Error disabling 2FA:', error);
      toast.error(message || t('admin.settings.toast.disableFailed'));
    }
  };

  const toggle2FA = () => {
    if (is2FAEnabled) {
      handleDisable2FA();
    } else {
      handleEnable2FA();
    }
  };

  if (loading) {
     return <div className="flex h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-8 py-8">
      {/* Page Header */}
      <div className="mb-8 space-y-1">
        <h2 className="text-4xl font-black tracking-tighter text-[#1A1A1A]">{t('admin.settings.title')}</h2>
        <p className="text-lg font-medium text-gray-500">{t('admin.settings.subtitle')}</p>
      </div>

      {/* Horizontal Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Profile Settings */}
        <div className="rounded-2xl border-2 border-black bg-white p-6 shadow-[4px_4px_0_#000] flex flex-col">
          <h3 className="mb-6 flex items-center gap-3 text-xl font-black">
            <Shield className="h-6 w-6" />
            {t('admin.settings.profileTitle')}
          </h3>
          
          <div className="flex-1 space-y-4">
            <div className="grid gap-2">
              <label className="text-sm font-bold text-gray-600">{t('admin.settings.displayName')}</label>
              <input 
                type="text" 
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border-2 border-black px-4 py-3 font-bold focus:shadow-[4px_4px_0_#000] focus:outline-none transition-shadow" 
              />
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-bold text-gray-600">{t('admin.settings.email')}</label>
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border-2 border-black px-4 py-3 font-bold focus:shadow-[4px_4px_0_#000] focus:outline-none transition-shadow" 
              />
              <p className="text-xs text-gray-500">{t('admin.settings.emailChangeNote')}</p>
            </div>
          </div>

          {/* Button - Right aligned */}
          <div className="flex justify-end pt-6 border-t border-gray-100 mt-6">
            <button 
              onClick={handleSaveProfile}
              disabled={saving}
              className="flex items-center gap-2 rounded-xl border-2 border-black bg-[#FFD93D] px-6 py-3 font-bold shadow-[4px_4px_0_#000] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#000] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
              {t('admin.settings.saveChanges')}
            </button>
          </div>
        </div>

        {/* Right: Security Settings */}
        <div className="rounded-2xl border-2 border-black bg-white p-6 shadow-[4px_4px_0_#000] flex flex-col">
          <h3 className="mb-6 flex items-center gap-3 text-xl font-black">
            <Key className="h-6 w-6" />
            {t('admin.settings.securityTitle')}
          </h3>

          <div className="flex-1 space-y-6">
            {/* 2FA Toggle */}
            <div className="flex items-center justify-between p-4 rounded-xl border-2 border-black bg-gray-50 mt-6">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-bold">{t('admin.settings.twoFactor')}</p>
                  {is2FAEnabled && (
                    <span className="rounded-full border-2 border-black bg-[#6FEDD6] px-2 py-0.5 text-xs font-bold">{t('admin.settings.twoFactorEnabled')}</span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">{t('admin.settings.twoFactorDesc')}</p>
              </div>
              <button 
                onClick={toggle2FA}
                className={cn(
                  "relative h-10 w-16 rounded-full border-2 border-black transition-colors duration-200 flex-shrink-0",
                  is2FAEnabled ? 'bg-[#6FEDD6]' : 'bg-gray-200'
                )}
              >
                <span
                  className={cn(
                    "absolute top-[4px] left-[4px] w-[26px] h-[26px] bg-white border-2 border-black rounded-full transition-transform duration-200",
                    is2FAEnabled ? "translate-x-6" : "translate-x-0"
                  )}
                />
              </button>
            </div>
          </div>

          {/* Button - Right aligned */}
          <div className="flex justify-end pt-6 border-t border-gray-100 mt-6">
            <Dialog open={isPasswordOpen} onOpenChange={setIsPasswordOpen}>
              <DialogTrigger asChild>
                <button className="flex items-center gap-2 rounded-xl border-2 border-black bg-white px-6 py-3 font-bold shadow-[4px_4px_0_#000] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#000] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all">
                  <Key className="h-5 w-5" />
                  {t('admin.settings.changePasswordButton')}
                </button>
              </DialogTrigger>
              <DialogContent aria-describedby="password-desc" className="sm:max-w-md border-2 border-black shadow-[8px_8px_0_#1A1A1A] bg-[#F5F0E8]">
                <DialogHeader>
                  <DialogTitle className="text-xl font-black">{t('admin.settings.changePassword')}</DialogTitle>
                  <DialogDescription id="password-desc">
                    {t('admin.settings.passwordDialogDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password" className="font-bold">{t('admin.settings.newPassword')}</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="border-2 border-black focus-visible:ring-0 focus-visible:shadow-[2px_2px_0_#000]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password" className="font-bold">{t('admin.settings.confirmNewPassword')}</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="border-2 border-black focus-visible:ring-0 focus-visible:shadow-[2px_2px_0_#000]"
                    />
                  </div>
                </div>
                <DialogFooter className="flex justify-end gap-3">
                  <button 
                    onClick={() => setIsPasswordOpen(false)}
                    className="px-4 py-2 rounded-lg border-2 border-black bg-white font-bold hover:bg-gray-100 transition-colors"
                  >
                    {t('admin.settings.cancel')}
                  </button>
                  <button 
                    onClick={handleChangePassword}
                    disabled={passwordLoading}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-black bg-[#6FEDD6] font-bold shadow-[2px_2px_0_#000] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[1px_1px_0_#000] active:shadow-none transition-all disabled:opacity-50"
                  >
                    {passwordLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {t('admin.settings.confirmChange')}
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* 2FA Setup Dialog */}
      <Dialog open={is2FASetupOpen} onOpenChange={setIs2FASetupOpen}>
        <DialogContent aria-describedby="2fa-desc" className="sm:max-w-md border-2 border-black shadow-[8px_8px_0_#1A1A1A] bg-[#F5F0E8]">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">{t('admin.settings.enable2FA')}</DialogTitle>
            <DialogDescription id="2fa-desc">
              {t('admin.settings.twoFactorSetupDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-6 py-4">
            {qrCodeUrl ? (
              <div className="rounded-xl border-2 border-black p-2 bg-white">
                <img src={qrCodeUrl} alt="2FA QR Code" className="h-48 w-48" />
              </div>
            ) : (
              <div className="flex h-48 w-48 items-center justify-center rounded-xl border-2 border-black bg-gray-100">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            )}
            
            <div className="w-full space-y-2">
              <Label htmlFor="verify-code" className="font-bold">{t('admin.settings.enter6DigitCode')}</Label>
              <Input
                id="verify-code"
                type="text"
                placeholder="000000"
                maxLength={6}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                className="border-2 border-black text-center text-lg font-bold tracking-widest focus-visible:ring-0 focus-visible:shadow-[2px_2px_0_#000]"
              />
            </div>
          </div>
          <DialogFooter className="flex justify-end gap-3">
            <button 
              onClick={() => setIs2FASetupOpen(false)}
              className="px-4 py-2 rounded-lg border-2 border-black bg-white font-bold hover:bg-gray-100 transition-colors"
            >
              {t('admin.settings.cancel')}
            </button>
            <button 
              onClick={handleVerify2FA}
              disabled={setupLoading || verifyCode.length !== 6}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-black bg-[#6FEDD6] font-bold shadow-[2px_2px_0_#000] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[1px_1px_0_#000] active:shadow-none transition-all disabled:opacity-50"
            >
              {setupLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('admin.settings.verifyAndEnable')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
